/**
 * 💰 Personal Budget Tracker — Excel-style monthly income/expense ledger.
 *
 * Multi-budget per user (sidebar dropdown switcher seeded with "Personal").
 * Each budget has its own entries: date, type, category, description, amount,
 * account, recurring flag. Monthly summary cards + pie (by category) + bar
 * (12-mo rollup). CSV import + export.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Download, Upload, Pencil, Wallet, TrendingUp,
  TrendingDown, PiggyBank, X, Check, Sparkles, Calendar, Eye, EyeOff,
  ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis, Legend, CartesianGrid,
} from "recharts";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIE_COLORS = [
  "#f97316", "#ef4444", "#eab308", "#22c55e", "#06b6d4",
  "#8b5cf6", "#ec4899", "#14b8a6", "#a3e635", "#f59e0b",
];

const fmt = (n, ccy = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(n || 0);

const todayISO = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

const emptyDraft = () => ({
  date: todayISO(),
  type: "expense",
  category: "",
  description: "",
  amount: "",
  account: "",
  recurring: false,
});

export default function BudgetPage() {
  const { isAuthed } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [activeBudgetId, setActiveBudgetId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [allEntries, setAllEntries] = useState([]);  // 🔮 unfiltered — powers the Look Ahead panel
  const [showLookAhead, setShowLookAhead] = useState(true);
  const [month, setMonth] = useState(currentMonth());
  const [showAll, setShowAll] = useState(false);  // when true, ignore month filter
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [showNewBudget, setShowNewBudget] = useState(false);
  const [newBudgetName, setNewBudgetName] = useState("");
  const csvFileRef = useRef(null);

  const activeBudget = useMemo(
    () => budgets.find((b) => b.budget_id === activeBudgetId),
    [budgets, activeBudgetId]
  );
  const currency = activeBudget?.currency || "USD";

  // ───── data loaders ─────
  const loadBudgets = async () => {
    try {
      const r = await axios.get(`${API}/budget/list`);
      const list = r.data?.budgets || [];
      setBudgets(list);
      if (list.length && !list.find((b) => b.budget_id === activeBudgetId)) {
        setActiveBudgetId(list[0].budget_id);
      }
    } catch {
      toast.error("Could not load your budgets.");
    }
  };

  const loadEntriesAndSummary = async () => {
    if (!activeBudgetId) return;
    try {
      const m = showAll ? "" : month;
      const qs = m ? `?month=${m}` : "";
      const [eRes, sRes, allRes] = await Promise.all([
        axios.get(`${API}/budget/${activeBudgetId}/entries${qs}`),
        axios.get(`${API}/budget/${activeBudgetId}/summary${qs}`),
        axios.get(`${API}/budget/${activeBudgetId}/entries`), // 🔮 unfiltered — powers Look Ahead
      ]);
      setEntries(eRes.data?.entries || []);
      setSummary(sRes.data || null);
      setAllEntries(allRes.data?.entries || []);
    } catch {
      toast.error("Could not load entries.");
    }
  };

  useEffect(() => { if (isAuthed) loadBudgets(); }, [isAuthed]);
  useEffect(() => { if (activeBudgetId) loadEntriesAndSummary(); }, [activeBudgetId, month, showAll]);

  // ───── mutations ─────
  const handleSuggestCategory = async () => {
    if (!activeBudgetId) return;
    if (!draft.description.trim()) {
      toast.error("Enter a description first so the AI can categorize it.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/budget/${activeBudgetId}/suggest-category`, {
        description: draft.description, type: draft.type,
      });
      if (r.data?.suggestion) {
        setDraft({ ...draft, category: r.data.suggestion });
        toast.success(`Suggested: ${r.data.suggestion}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not suggest a category.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddEntry = async () => {
    if (!activeBudgetId) return;
    if (!draft.amount || Number(draft.amount) <= 0) {
      toast.error("Enter an amount greater than 0.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/budget/${activeBudgetId}/entries`, draft);
      setDraft(emptyDraft());
      await loadEntriesAndSummary();
      toast.success("Entry added.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add entry.");
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateEntry = async () => {
    if (!editEntry?.entry_id) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/budget/entries/${editEntry.entry_id}`, editEntry);
      setEditEntry(null);
      await loadEntriesAndSummary();
      toast.success("Entry updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update entry.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/budget/entries/${id}`);
      await loadEntriesAndSummary();
      toast.success("Entry deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    }
  };

  const handleCreateBudget = async () => {
    const name = newBudgetName.trim();
    if (!name) { toast.error("Name your budget."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/budget/create`, { name });
      setNewBudgetName("");
      setShowNewBudget(false);
      await loadBudgets();
      setActiveBudgetId(r.data.budget_id);
      toast.success(`Created "${r.data.name}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create budget.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteBudget = async () => {
    if (!activeBudget) return;
    if (budgets.length === 1) {
      toast.error("You need at least one budget. Create another first.");
      return;
    }
    if (!window.confirm(`Delete budget "${activeBudget.name}" and ALL its entries? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/budget/${activeBudgetId}`);
      const remaining = budgets.filter((b) => b.budget_id !== activeBudgetId);
      setBudgets(remaining);
      setActiveBudgetId(remaining[0]?.budget_id || null);
      toast.success("Budget deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete budget.");
    }
  };

  const handleExportCsv = async () => {
    if (!activeBudgetId) return;
    try {
      const m = showAll ? "" : month;
      const qs = m ? `?month=${m}` : "";
      const r = await axios.get(`${API}/budget/${activeBudgetId}/export-csv${qs}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeBudget?.name || "budget"}${m ? `-${m}` : ""}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export CSV.");
    }
  };

  const handleImportCsv = async (file) => {
    if (!file || !activeBudgetId) return;
    const text = await file.text();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/budget/${activeBudgetId}/import-csv`, { csv_text: text });
      const { inserted, errors } = r.data || {};
      toast.success(`Imported ${inserted} row(s).${errors?.length ? ` ${errors.length} skipped.` : ""}`);
      await loadEntriesAndSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(false);
      if (csvFileRef.current) csvFileRef.current.value = "";
    }
  };

  // ───── render ─────
  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">💰 Budget</h1>
          <p className="text-zinc-400">Sign in to track your income and expenses.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const pieData = (summary?.by_category || []).slice(0, 10);
  const barData = summary?.by_month || [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="budget-page">
        {/* ───── Top bar: budget switcher + actions ───── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Wallet className="w-7 h-7 text-orange-400" />
            <h1 className="text-3xl font-bold">Budget</h1>
            {budgets.length > 0 && (
              <Select value={activeBudgetId || ""} onValueChange={setActiveBudgetId}>
                <SelectTrigger className="w-56 bg-zinc-900 border-zinc-800" data-testid="budget-switcher">
                  <SelectValue placeholder="Select budget" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 text-zinc-100">
                  {budgets.map((b) => (
                    <SelectItem key={b.budget_id} value={b.budget_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              size="sm" variant="outline"
              className="border-zinc-700"
              onClick={() => setShowNewBudget(true)}
              data-testid="new-budget-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Budget
            </Button>
            {activeBudget && budgets.length > 1 && (
              <Button
                size="sm" variant="ghost"
                className="text-red-400 hover:text-red-300"
                onClick={handleDeleteBudget}
                data-testid="delete-budget-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="month"
              value={month}
              onChange={(e) => { setShowAll(false); setMonth(e.target.value); }}
              className="w-44 bg-zinc-900 border-zinc-800"
              data-testid="month-picker"
              disabled={showAll}
            />
            <Button
              size="sm"
              variant={showAll ? "default" : "outline"}
              className={showAll ? "bg-orange-500 hover:bg-orange-600" : "border-zinc-700"}
              onClick={() => setShowAll((v) => !v)}
              data-testid="show-all-toggle"
            >
              {showAll ? "Showing All Time" : "All Time"}
            </Button>
            <Button
              size="sm" variant="outline" className="border-zinc-700"
              onClick={handleExportCsv}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button
              size="sm" variant="outline" className="border-zinc-700"
              onClick={() => csvFileRef.current?.click()}
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-1" /> Import CSV
            </Button>
            <input
              ref={csvFileRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => handleImportCsv(e.target.files?.[0])}
              data-testid="csv-file-input"
            />
          </div>
        </div>

        {/* ───── 🔮 Look Ahead — upcoming recurring bills/income + spending trends ───── */}
        <LookAheadPanel
          allEntries={allEntries}
          currency={currency}
          open={showLookAhead}
          onToggle={() => setShowLookAhead((v) => !v)}
        />

        {/* ───── Summary cards ───── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <SummaryCard
            label="Income"
            value={summary?.income}
            currency={currency}
            tone="green"
            icon={<TrendingUp className="w-5 h-5" />}
            testid="summary-income"
          />
          <SummaryCard
            label="Expenses"
            value={summary?.expenses}
            currency={currency}
            tone="red"
            icon={<TrendingDown className="w-5 h-5" />}
            testid="summary-expenses"
          />
          <SummaryCard
            label="Net"
            value={summary?.net}
            currency={currency}
            tone={summary?.net >= 0 ? "green" : "red"}
            icon={<PiggyBank className="w-5 h-5" />}
            testid="summary-net"
          />
        </div>

        {/* ───── Charts ───── */}
        {(pieData.length > 0 || barData.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {pieData.length > 0 && (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-base">Expenses by Category</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="amount"
                        nameKey="category"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RTooltip
                        contentStyle={{ background: "#18181b", border: "1px solid #3f3f46" }}
                        formatter={(v) => fmt(v, currency)}
                      />
                      <Legend wrapperStyle={{ color: "#a1a1aa", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            {barData.length > 0 && (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-base">Monthly Rollup</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
                      <YAxis stroke="#a1a1aa" fontSize={11} />
                      <RTooltip
                        contentStyle={{ background: "#18181b", border: "1px solid #3f3f46" }}
                        formatter={(v) => fmt(v, currency)}
                      />
                      <Legend wrapperStyle={{ color: "#a1a1aa", fontSize: 12 }} />
                      <Bar dataKey="income" fill="#22c55e" />
                      <Bar dataKey="expense" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ───── Add-row form ───── */}
        <Card className="bg-zinc-900 border-zinc-800 mb-4">
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-8 gap-2">
              <Input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                className="bg-zinc-950 border-zinc-800"
                data-testid="add-entry-date"
              />
              <Select value={draft.type} onValueChange={(v) => setDraft({ ...draft, type: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="add-entry-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 text-zinc-100">
                  <SelectItem value="expense">Expense</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Category"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="bg-zinc-950 border-zinc-800"
                data-testid="add-entry-category"
              />
              <div className="flex gap-1 md:col-span-2">
                <Input
                  placeholder="Description"
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 flex-1"
                  data-testid="add-entry-description"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="border-zinc-700 hover:bg-amber-500/10 hover:border-amber-500/50 hover:text-amber-300 shrink-0"
                  onClick={handleSuggestCategory}
                  disabled={busy || !draft.description.trim()}
                  title="AI: Suggest category from description"
                  data-testid="suggest-category-btn"
                >
                  <Sparkles className="w-4 h-4" />
                </Button>
              </div>
              <Input
                placeholder="Account"
                value={draft.account}
                onChange={(e) => setDraft({ ...draft, account: e.target.value })}
                className="bg-zinc-950 border-zinc-800"
                data-testid="add-entry-account"
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Amount"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                className="bg-zinc-950 border-zinc-800"
                data-testid="add-entry-amount"
              />
              <Button
                onClick={handleAddEntry}
                disabled={busy}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="add-entry-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-400 mt-2 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={draft.recurring}
                onChange={(e) => setDraft({ ...draft, recurring: e.target.checked })}
                data-testid="add-entry-recurring"
              />
              Recurring monthly
            </label>
          </CardContent>
        </Card>

        {/* ───── Entries table ───── */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="entries-table">
              <thead className="bg-zinc-950 text-zinc-400 uppercase text-xs">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-zinc-500 py-10" data-testid="empty-state">
                      No entries yet. Add your first transaction above, or import a CSV.
                    </td>
                  </tr>
                )}
                {entries.map((e) => (
                  <tr
                    key={e.entry_id}
                    className="border-t border-zinc-800 hover:bg-zinc-800/40"
                    data-testid={`entry-row-${e.entry_id}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        e.type === "income" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"
                      }`}>
                        {e.type}
                      </span>
                      {e.recurring && <span className="ml-1 text-[10px] text-zinc-500">↻</span>}
                    </td>
                    <td className="px-3 py-2">{e.category || <span className="text-zinc-600">—</span>}</td>
                    <td className="px-3 py-2 text-zinc-300">{e.description || <span className="text-zinc-600">—</span>}</td>
                    <td className="px-3 py-2 text-zinc-400">{e.account || <span className="text-zinc-600">—</span>}</td>
                    <td className={`px-3 py-2 text-right font-mono ${
                      e.type === "income" ? "text-green-400" : "text-red-400"
                    }`}>
                      {e.type === "income" ? "+" : "−"}{fmt(e.amount, currency)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-100"
                        onClick={() => setEditEntry({ ...e })}
                        data-testid={`edit-entry-${e.entry_id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteEntry(e.entry_id)}
                        data-testid={`delete-entry-${e.entry_id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* ───── New Budget dialog ───── */}
      <Dialog open={showNewBudget} onOpenChange={setShowNewBudget}>
        <DialogContent className="bg-zinc-900 text-zinc-100 border-zinc-800">
          <DialogHeader>
            <DialogTitle>New Budget</DialogTitle>
          </DialogHeader>
          <Input
            placeholder='e.g. "Business", "Vacation", "Household"'
            value={newBudgetName}
            onChange={(e) => setNewBudgetName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateBudget()}
            className="bg-zinc-950 border-zinc-800"
            data-testid="new-budget-name-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewBudget(false)}>Cancel</Button>
            <Button
              onClick={handleCreateBudget}
              disabled={busy}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-new-budget-btn"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───── Edit Entry dialog ───── */}
      <Dialog open={!!editEntry} onOpenChange={(open) => !open && setEditEntry(null)}>
        <DialogContent className="bg-zinc-900 text-zinc-100 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Edit Entry</DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-400">Date</label>
                  <Input
                    type="date"
                    value={editEntry.date}
                    onChange={(e) => setEditEntry({ ...editEntry, date: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400">Type</label>
                  <Select value={editEntry.type} onValueChange={(v) => setEditEntry({ ...editEntry, type: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 text-zinc-100">
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-400">Category</label>
                  <Input
                    value={editEntry.category}
                    onChange={(e) => setEditEntry({ ...editEntry, category: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400">Account</label>
                  <Input
                    value={editEntry.account}
                    onChange={(e) => setEditEntry({ ...editEntry, account: e.target.value })}
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-400">Description</label>
                <Input
                  value={editEntry.description}
                  onChange={(e) => setEditEntry({ ...editEntry, description: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400">Amount</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editEntry.amount}
                  onChange={(e) => setEditEntry({ ...editEntry, amount: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editEntry.recurring}
                  onChange={(e) => setEditEntry({ ...editEntry, recurring: e.target.checked })}
                />
                Recurring monthly
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditEntry(null)}>Cancel</Button>
            <Button
              onClick={handleUpdateEntry}
              disabled={busy}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-edit-btn"
            >
              <Check className="w-4 h-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function SummaryCard({ label, value, currency, tone, icon, testid }) {
  const ring =
    tone === "green" ? "ring-green-500/20" :
    tone === "red"   ? "ring-red-500/20"   :
                       "ring-zinc-700";
  const text =
    tone === "green" ? "text-green-400" :
    tone === "red"   ? "text-red-400"   :
                       "text-zinc-100";
  return (
    <Card className={`bg-zinc-900 border-zinc-800 ring-1 ${ring}`} data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wide">
          {icon}{label}
        </div>
        <div className={`text-3xl font-bold mt-2 ${text} font-mono`}>
          {fmt(value, currency)}
        </div>
      </CardContent>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────
 * 🔮 LookAheadPanel — Forecast next 30 days from recurring entries
 *    + spending-trend arrows vs last 3 months per category.
 *    Frontend-only projection (no backend forecast endpoint needed).
 * ──────────────────────────────────────────────────────────── */
function LookAheadPanel({ allEntries, currency, open, onToggle }) {
  const forecast = useMemo(() => {
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 86400000);

    // ─── Upcoming projected occurrences from recurring entries ───
    // Monthly cadence: next occurrence = same day-of-month as the LAST time
    // that recurring entry appeared, projected into the next 30-day window.
    const recurringMap = new Map();
    for (const e of allEntries) {
      if (!e.recurring) continue;
      const key = `${e.type}|${e.category}|${e.description}|${e.amount}`;
      const cur = recurringMap.get(key);
      const d = new Date(e.date);
      if (!cur || d > cur.lastDate) {
        recurringMap.set(key, { ...e, lastDate: d });
      }
    }
    const upcoming = [];
    for (const rec of recurringMap.values()) {
      // Project the same day-of-month FORWARD until we're past today+30
      const seed = new Date(rec.lastDate);
      seed.setMonth(seed.getMonth() + 1);   // next cycle
      let guard = 0;
      while (seed <= in30 && guard++ < 6) {
        if (seed >= now) {
          upcoming.push({
            date: seed.toISOString().slice(0, 10),
            type: rec.type,
            category: rec.category,
            description: rec.description,
            amount: Number(rec.amount) || 0,
          });
        }
        seed.setMonth(seed.getMonth() + 1);
      }
    }
    upcoming.sort((a, b) => (a.date < b.date ? -1 : 1));

    // Projected totals
    const projIncome = upcoming
      .filter((u) => u.type === "income")
      .reduce((s, u) => s + u.amount, 0);
    const projExpenses = upcoming
      .filter((u) => u.type === "expense")
      .reduce((s, u) => s + u.amount, 0);

    // ─── Spending trend vs last 3 completed months (top expense categories) ───
    const thisMonthISO = now.toISOString().slice(0, 7);
    const priorMonthKeys = [];
    for (let i = 1; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      priorMonthKeys.push(d.toISOString().slice(0, 7));
    }
    // Sum expenses per category per month
    const catMonthly = new Map();  // category → { prior: [m1,m2,m3], current: n }
    for (const e of allEntries) {
      if (e.type !== "expense" || !e.category) continue;
      const ym = String(e.date).slice(0, 7);
      const bucket = catMonthly.get(e.category) || { prior: [], current: 0 };
      if (ym === thisMonthISO) bucket.current += Number(e.amount) || 0;
      else if (priorMonthKeys.includes(ym)) bucket.prior.push(Number(e.amount) || 0);
      catMonthly.set(e.category, bucket);
    }
    // Compute 3-month average vs current-month pace
    const daysElapsed = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const paceMult = daysElapsed > 0 ? daysInMonth / daysElapsed : 1;
    const trends = [];
    for (const [cat, { prior, current }] of catMonthly.entries()) {
      const priorSum = prior.reduce((s, n) => s + n, 0);
      const avg3mo = prior.length > 0 ? priorSum / 3 : 0;  // divide by 3 months even if some are $0
      const projectedCurrent = current * paceMult;
      let delta = 0, arrow = "flat";
      if (avg3mo > 0) {
        delta = ((projectedCurrent - avg3mo) / avg3mo) * 100;
        if (delta > 10) arrow = "up";
        else if (delta < -10) arrow = "down";
      } else if (projectedCurrent > 0) {
        arrow = "up";
        delta = 100;
      }
      trends.push({ category: cat, avg3mo, projectedCurrent, delta, arrow });
    }
    // Sort by projected current descending, take top 5
    trends.sort((a, b) => b.projectedCurrent - a.projectedCurrent);

    return {
      upcoming: upcoming.slice(0, 12),  // cap for UI
      projIncome,
      projExpenses,
      projNet: projIncome - projExpenses,
      trends: trends.slice(0, 5),
    };
  }, [allEntries]);

  const hasData =
    forecast.upcoming.length > 0 || forecast.trends.length > 0;

  return (
    <Card
      className="bg-gradient-to-br from-indigo-950/40 via-zinc-900 to-purple-950/30 border-indigo-500/30 mb-6"
      data-testid="look-ahead-panel"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2 text-indigo-200">
            <Calendar className="w-5 h-5 text-indigo-300" />
            Look Ahead — Next 30 Days
          </CardTitle>
          <Button
            size="sm"
            variant="ghost"
            className="text-indigo-300 hover:text-white h-7 px-2"
            onClick={onToggle}
            data-testid="look-ahead-toggle"
          >
            {open ? <><EyeOff className="w-4 h-4 mr-1" /> Hide</> : <><Eye className="w-4 h-4 mr-1" /> Show</>}
          </Button>
        </div>
      </CardHeader>

      {open && (
        <CardContent className="pt-0 space-y-4">
          {!hasData ? (
            <div className="text-sm text-zinc-400 italic text-center py-4" data-testid="look-ahead-empty">
              Mark entries as <span className="text-indigo-300 font-semibold">recurring</span> to see upcoming projections here.
              Trend arrows appear once you have expenses in the last 3 months.
            </div>
          ) : (
            <>
              {/* Projected totals strip */}
              <div className="grid grid-cols-3 gap-2">
                <MiniStat
                  label="Projected Income"
                  value={forecast.projIncome}
                  currency={currency}
                  tone="green"
                  testid="look-ahead-proj-income"
                />
                <MiniStat
                  label="Projected Bills"
                  value={forecast.projExpenses}
                  currency={currency}
                  tone="red"
                  testid="look-ahead-proj-expenses"
                />
                <MiniStat
                  label="Projected Net"
                  value={forecast.projNet}
                  currency={currency}
                  tone={forecast.projNet >= 0 ? "green" : "red"}
                  testid="look-ahead-proj-net"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Upcoming recurring list */}
                <div className="rounded-lg border border-zinc-800 bg-black/30 p-3" data-testid="look-ahead-upcoming">
                  <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-2">
                    📅 Upcoming Recurring
                  </div>
                  {forecast.upcoming.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">
                      No recurring entries projected in the next 30 days.
                    </div>
                  ) : (
                    <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                      {forecast.upcoming.map((u, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between gap-2 text-xs border-b border-zinc-900 pb-1.5 last:border-0"
                          data-testid={`look-ahead-upcoming-${i}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-zinc-500 text-[10px] tabular-nums whitespace-nowrap">
                                {u.date.slice(5)}
                              </span>
                              <span className="text-zinc-200 truncate">
                                {u.description || u.category || "(no label)"}
                              </span>
                            </div>
                            {u.category && u.description && (
                              <div className="text-[10px] text-zinc-500 pl-11">{u.category}</div>
                            )}
                          </div>
                          <div className={`font-mono text-xs whitespace-nowrap ${u.type === "income" ? "text-green-400" : "text-red-400"}`}>
                            {u.type === "income" ? "+" : "−"}{fmt(u.amount, currency)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Spending-trend arrows */}
                <div className="rounded-lg border border-zinc-800 bg-black/30 p-3" data-testid="look-ahead-trends">
                  <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-2">
                    📈 Spending Trend · this month pace vs 3-mo avg
                  </div>
                  {forecast.trends.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">
                      Not enough historical data yet — trends appear after ~1 month of expenses.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {forecast.trends.map((t, i) => (
                        <li
                          key={t.category}
                          className="flex items-center justify-between gap-2"
                          data-testid={`look-ahead-trend-${i}`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <TrendArrow arrow={t.arrow} />
                            <span className="text-xs text-zinc-200 truncate">{t.category}</span>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <div className="text-xs text-zinc-300 font-mono">
                              {fmt(t.projectedCurrent, currency)}
                            </div>
                            <div className={`text-[10px] font-mono ${t.arrow === "up" ? "text-red-400" : t.arrow === "down" ? "text-green-400" : "text-zinc-500"}`}>
                              {t.arrow === "flat" ? "≈ 3-mo avg" : `${t.delta > 0 ? "+" : ""}${t.delta.toFixed(0)}% vs 3-mo avg`}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="text-[10px] text-zinc-500 italic text-center pt-1">
                Projection uses monthly cadence from your <span className="text-indigo-400">recurring</span> entries · trend arrows compare this month&apos;s pace to your 3-month average.
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function MiniStat({ label, value, currency, tone, testid }) {
  const text =
    tone === "green" ? "text-green-400" :
    tone === "red"   ? "text-red-400"   :
                       "text-zinc-100";
  return (
    <div className="rounded-md border border-zinc-800 bg-black/40 p-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-sm sm:text-base font-bold ${text}`}>
        {fmt(value, currency)}
      </div>
    </div>
  );
}

function TrendArrow({ arrow }) {
  if (arrow === "up")   return <ArrowUpRight   className="w-4 h-4 text-red-400 shrink-0" />;
  if (arrow === "down") return <ArrowDownRight className="w-4 h-4 text-green-400 shrink-0" />;
  return <Minus className="w-4 h-4 text-zinc-500 shrink-0" />;
}

